export type GrowthSegmentMatchMode = 'all' | 'any';

export type GrowthSegmentRuleField =
  | 'lifecycleStage'
  | 'tag'
  | 'leadStatus'
  | 'leadScoreMin'
  | 'activityType'
  | 'channelKey'
  | 'industryKey';

export type GrowthSegmentRuleOperator = 'eq' | 'gte';

export interface GrowthSegmentRuleConditionForm {
  localId: string;
  field: GrowthSegmentRuleField;
  op: GrowthSegmentRuleOperator;
  value: string;
}

export interface GrowthSegmentRuleForm {
  match: GrowthSegmentMatchMode;
  conditions: GrowthSegmentRuleConditionForm[];
}

const allowedFields: GrowthSegmentRuleField[] = [
  'lifecycleStage',
  'tag',
  'leadStatus',
  'leadScoreMin',
  'activityType',
  'channelKey',
  'industryKey'
];

export function createDefaultSegmentRuleForm(): GrowthSegmentRuleForm {
  return {
    match: 'all',
    conditions: [createSegmentRuleCondition()]
  };
}

export function createSegmentRuleCondition(): GrowthSegmentRuleConditionForm {
  return {
    localId: crypto.randomUUID(),
    field: 'lifecycleStage',
    op: 'eq',
    value: 'Prospect'
  };
}

export function parseSegmentRuleJson(ruleDefinitionJson: string): GrowthSegmentRuleForm {
  try {
    const parsed = JSON.parse(ruleDefinitionJson) as {
      match?: string;
      conditions?: Array<{ field?: string; op?: string; value?: unknown }>;
    };
    const match: GrowthSegmentMatchMode = parsed.match === 'any' ? 'any' : 'all';
    const conditions: GrowthSegmentRuleConditionForm[] = (parsed.conditions ?? [])
      .map(item => ({
        localId: crypto.randomUUID(),
        field: normalizeField(item.field),
        op: (item.op === 'gte' ? 'gte' : 'eq') as GrowthSegmentRuleOperator,
        value: item.value == null ? '' : String(item.value)
      }))
      .filter(item => allowedFields.includes(item.field));

    return {
      match,
      conditions: conditions.length > 0 ? conditions : [createSegmentRuleCondition()]
    };
  } catch {
    return createDefaultSegmentRuleForm();
  }
}

export function serializeSegmentRuleForm(form: GrowthSegmentRuleForm): string {
  return JSON.stringify({
    match: form.match,
    conditions: form.conditions.map(item => ({
      field: item.field,
      op: item.op,
      value: coerceConditionValue(item)
    }))
  });
}

export function validateSegmentRuleForm(form: GrowthSegmentRuleForm): string | null {
  if (!form.conditions.length)
    return 'growth.crm.segments.ruleValidation.conditionsRequired';

  for (const condition of form.conditions) {
    if (!allowedFields.includes(condition.field))
      return 'growth.crm.segments.ruleValidation.fieldNotAllowed';

    if (!condition.value.trim())
      return 'growth.crm.segments.ruleValidation.valueRequired';

    if (condition.field === 'leadScoreMin') {
      const score = Number(condition.value);
      if (Number.isNaN(score))
        return 'growth.crm.segments.ruleValidation.scoreMustBeNumber';
    }
  }

  return null;
}

function normalizeField(field?: string): GrowthSegmentRuleField {
  return allowedFields.includes(field as GrowthSegmentRuleField)
    ? field as GrowthSegmentRuleField
    : 'lifecycleStage';
}

function coerceConditionValue(condition: GrowthSegmentRuleConditionForm): string | number {
  if (condition.field === 'leadScoreMin') {
    const parsed = Number(condition.value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return condition.value.trim();
}

export function formatSegmentMemberReason(reasonJson: string): string {
  try {
    const parsed = JSON.parse(reasonJson) as { matchedFields?: string[] };
    if (!parsed.matchedFields?.length)
      return reasonJson;
    return parsed.matchedFields.join(', ');
  } catch {
    return reasonJson;
  }
}
